import React, {
    useEffect,
    useState,
} from "react";

import api, {
    BACKEND_URL,
    showAlert,
} from "../../util/api";


const emptyForm = {
    impact_id: "",
    title: "",
    tag: "",
    description: "",
    content: "",
    location: "",
    project_year: "",
    status: "ongoing",
    featured: false,
    active: true,
    image: null,
};


const AdminProjects = () => {

    const [projects, setProjects] = useState([]);

    const [impacts, setImpacts] = useState([]);

    const [form, setForm] = useState(
        emptyForm
    );

    const [editingId, setEditingId] =
        useState(null);

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [message, setMessage] =
        useState("");

    const [preview, setPreview] =
        useState(null);


    /*
    |--------------------------------------------------------------------------
    | MEDIA
    |--------------------------------------------------------------------------
    */

    const [media, setMedia] =
        useState([]);

    const [mediaLoading, setMediaLoading] =
        useState(false);

    const [mediaUploading, setMediaUploading] =
        useState(false);

    const [mediaOverallProgress, setMediaOverallProgress] =
        useState(0);

    const [mediaDragId, setMediaDragId] =
        useState(null);

    const [mediaDragOverId, setMediaDragOverId] =
        useState(null);


    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const [pagination, setPagination] =
        useState({
            current_page: 1,
            last_page: 1,
            per_page: 15,
            total: 0,
            from: 0,
            to: 0,
        });

    const [currentPage, setCurrentPage] =
        useState(1);

    const [perPage, setPerPage] =
        useState(15);


    /*
    |--------------------------------------------------------------------------
    | PROJECT DRAG/DROP
    |--------------------------------------------------------------------------
    */

    const [draggedId, setDraggedId] =
        useState(null);

    const [dragOverId, setDragOverId] =
        useState(null);

    const [reordering, setReordering] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | PENDING MEDIA
    |
    | Files selected while creating a new project.
    |--------------------------------------------------------------------------
    */

    const [pendingMedia, setPendingMedia] =
        useState([]);


    useEffect(() => {
        fetchData(
            currentPage,
            perPage
        );
    }, [
        currentPage,
        perPage,
    ]);


    /*
    |--------------------------------------------------------------------------
    | FETCH PROJECTS
    |--------------------------------------------------------------------------
    */

    const fetchData = async (
        page = currentPage,
        limit = perPage
    ) => {

        try {

            setLoading(true);

            const [
                projectsResponse,
                impactsResponse,
            ] = await Promise.all([

                api.get(
                    `/admin/projects?page=${page}&per_page=${limit}`
                ),

                api.get(
                    "/admin/impact"
                ),

            ]);

            const projectsResult =
                projectsResponse.data;

            const impactsResult =
                impactsResponse.data;


            if (
                projectsResult.success
            ) {

                const paginated =
                    projectsResult.data;

                setProjects(
                    paginated.data || []
                );

                setPagination({

                    current_page:
                        paginated.current_page ||
                        page,

                    last_page:
                        paginated.last_page ||
                        1,

                    per_page:
                        paginated.per_page ||
                        limit,

                    total:
                        paginated.total ||
                        0,

                    from:
                        paginated.from ||
                        0,

                    to:
                        paginated.to ||
                        0,

                });

            }


            if (
                impactsResult.success
            ) {

                setImpacts(
                    impactsResult.data ||
                    []
                );

            }

        } catch (error) {

            console.error(
                "Error loading projects:",
                error
            );

            setMessage(
                error?.response?.data?.message ||
                error.message ||
                "Unable to load projects."
            );

        } finally {

            setLoading(false);

        }
    };


    /*
    |--------------------------------------------------------------------------
    | PAGE
    |--------------------------------------------------------------------------
    */

    const handlePageChange = (
        page
    ) => {

        if (
            page < 1 ||
            page > pagination.last_page ||
            page === currentPage
        ) {
            return;
        }

        setCurrentPage(page);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };


    const handlePerPageChange = (
        e
    ) => {

        const value =
            Number(
                e.target.value
            );

        setPerPage(value);

        setCurrentPage(1);
    };


    const getPageNumbers = () => {

        const totalPages =
            pagination.last_page || 1;

        const page =
            currentPage;

        if (
            totalPages <= 7
        ) {

            return Array.from(
                {
                    length:
                        totalPages,
                },
                (_, index) =>
                    index + 1
            );

        }

        const pages =
            new Set([
                1,
                totalPages,
                page,
                page - 1,
                page + 1,
            ]);

        return Array.from(pages)
            .filter(
                (item) =>
                    item >= 1 &&
                    item <= totalPages
            )
            .sort(
                (a, b) => a - b
            );
    };


    /*
    |--------------------------------------------------------------------------
    | PROJECT DRAG/DROP
    |--------------------------------------------------------------------------
    */

    const handleDragStart = (
        e,
        projectId
    ) => {

        setDraggedId(
            projectId
        );

        e.dataTransfer.effectAllowed =
            "move";

        e.dataTransfer.setData(
            "text/plain",
            String(projectId)
        );
    };


    const handleDragOver = (
        e,
        projectId
    ) => {

        e.preventDefault();

        e.dataTransfer.dropEffect =
            "move";

        if (
            draggedId &&
            draggedId !== projectId
        ) {
            setDragOverId(
                projectId
            );
        }
    };


    const handleDragLeave = () => {
        setDragOverId(null);
    };


    const handleDrop = async (
        e,
        targetId
    ) => {

        e.preventDefault();

        const sourceId =
            draggedId ||
            Number(
                e.dataTransfer.getData(
                    "text/plain"
                )
            );

        setDragOverId(null);

        if (
            !sourceId ||
            sourceId === targetId
        ) {
            setDraggedId(null);
            return;
        }

        const sourceIndex =
            projects.findIndex(
                (item) =>
                    Number(item.id) ===
                    Number(sourceId)
            );

        const targetIndex =
            projects.findIndex(
                (item) =>
                    Number(item.id) ===
                    Number(targetId)
            );

        if (
            sourceIndex === -1 ||
            targetIndex === -1
        ) {
            setDraggedId(null);
            return;
        }

        const reordered = [
            ...projects,
        ];

        const [
            movedProject
        ] =
            reordered.splice(
                sourceIndex,
                1
            );

        const adjustedTargetIndex =
            sourceIndex < targetIndex
                ? targetIndex - 1
                : targetIndex;

        reordered.splice(
            adjustedTargetIndex,
            0,
            movedProject
        );

        setProjects(
            reordered
        );

        setDraggedId(null);


        try {

            setReordering(true);

            setMessage("");

            const items =
                reordered.map(
                    (
                        project,
                        index
                    ) => ({

                        id:
                            project.id,

                        sort_order:
                            (
                                (
                                    pagination.current_page -
                                    1
                                ) *
                                pagination.per_page
                            ) +
                            index,

                    })
                );


            const response =
                await api.post(
                    "/admin/projects/reorder",
                    {
                        items,
                    }
                );

            const result =
                response.data;

            if (
                !result.success
            ) {

                throw new Error(
                    result.message ||
                    "Unable to reorder projects."
                );

            }

            setMessage(
                "Project order updated successfully."
            );

        } catch (error) {

            setMessage(
                error?.response?.data?.message ||
                error.message ||
                "Unable to reorder projects."
            );

            await fetchData(
                currentPage,
                perPage
            );

        } finally {

            setReordering(false);

        }
    };


    const handleDragEnd = () => {

        setDraggedId(null);

        setDragOverId(null);
    };


    /*
    |--------------------------------------------------------------------------
    | FORM
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        e
    ) => {

        const {
            name,
            value,
            type,
            checked,
            files,
        } = e.target;


        if (
            name === "image"
        ) {

            const file =
                files?.[0] ||
                null;

            setForm(
                (prev) => ({
                    ...prev,
                    image: file,
                })
            );


            if (file) {

                setPreview(
                    URL.createObjectURL(
                        file
                    )
                );

            }

            return;
        }


        setForm(
            (prev) => ({

                ...prev,

                [name]:
                    type === "checkbox"
                        ? checked
                        : value,

            })
        );
    };


    /*
    |--------------------------------------------------------------------------
    | RESET FORM
    |--------------------------------------------------------------------------
    */

    const resetForm = () => {

        setForm({
            ...emptyForm,
        });

        setEditingId(null);

        setPreview(null);

        setMedia([]);

        setPendingMedia([]);

        setMediaOverallProgress(0);
    };


    /*
    |--------------------------------------------------------------------------
    | MEDIA HELPERS
    |--------------------------------------------------------------------------
    */

    const formatBytes = (
        bytes
    ) => {

        if (
            !bytes ||
            bytes === 0
        ) {
            return "0 B";
        }

        const units = [
            "B",
            "KB",
            "MB",
            "GB",
        ];

        const index =
            Math.floor(
                Math.log(bytes) /
                Math.log(1024)
            );

        return (
            parseFloat(
                (
                    bytes /
                    Math.pow(
                        1024,
                        index
                    )
                ).toFixed(1)
            ) +
            " " +
            units[index]
        );
    };


    const getMediaUrl = (
        item
    ) => {

        if (!item) {
            return "";
        }

        if (
            item.preview
        ) {
            return item.preview;
        }

        if (
            item.file_path
        ) {
            return `${BACKEND_URL}/storage/${item.file_path}`;
        }

        return "";
    };


    const getFileType = (
        file
    ) => {

        if (
            file.type?.startsWith(
                "image/"
            )
        ) {
            return "image";
        }

        if (
            file.type?.startsWith(
                "video/"
            )
        ) {
            return "video";
        }

        return "document";
    };


    /*
    |--------------------------------------------------------------------------
    | SELECT MEDIA
    |--------------------------------------------------------------------------
    */

    const handleMediaSelection = (
        e
    ) => {

        const files =
            Array.from(
                e.target.files || []
            );

        if (!files.length) {
            return;
        }


        const newFiles =
            files.map(
                (
                    file,
                    index
                ) => ({

                    id:
                        `pending-${Date.now()}-${index}-${Math.random()}`,

                    file,

                    type:
                        getFileType(file),

                    display_name:
                        file.name.replace(
                            /\.[^/.]+$/,
                            ""
                        ),

                    status:
                        "pending",

                    progress:
                        0,

                    preview:
                        file.type.startsWith(
                            "image/"
                        )
                            ? URL.createObjectURL(
                                file
                            )
                            : null,

                    error:
                        null,

                })
            );


        setPendingMedia(
            (prev) => [
                ...prev,
                ...newFiles,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | If editing an existing project, upload immediately.
        |--------------------------------------------------------------------------
        */

        if (
            editingId
        ) {

            newFiles.forEach(
                (item) => {

                    uploadMediaFile(
                        editingId,
                        item
                    );

                }
            );

        }


        e.target.value = "";
    };


    /*
    |--------------------------------------------------------------------------
    | REMOVE PENDING FILE
    |--------------------------------------------------------------------------
    */

    const removePendingMedia = (
        id
    ) => {

        setPendingMedia(
            (prev) =>
                prev.filter(
                    (item) =>
                        item.id !== id
                )
        );
    };


    /*
    |--------------------------------------------------------------------------
    | UPLOAD SINGLE MEDIA
    |--------------------------------------------------------------------------
    */

    const uploadMediaFile = async (
        projectId,
        item
    ) => {

        if (
            !item?.file
        ) {
            return;
        }


        setMediaUploading(true);


        setPendingMedia(
            (prev) =>
                prev.map(
                    (file) =>
                        file.id === item.id
                            ? {
                                ...file,
                                status:
                                    "uploading",
                                progress:
                                    0,
                                error:
                                    null,
                            }
                            : file
                )
        );


        const formData =
            new FormData();

        formData.append(
            "file",
            item.file
        );

        formData.append(
            "display_name",
            item.display_name ||
            item.file.name
        );

        formData.append(
            "type",
            item.type
        );


        try {

            const response =
                await api.post(
                    `/admin/projects/${projectId}/media`,
                    formData,
                    {

                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },

                        onUploadProgress:
                            (event) => {

                                const total =
                                    event.total ||
                                    item.file.size;

                                const progress =
                                    total
                                        ? Math.round(
                                            (
                                                event.loaded /
                                                total
                                            ) *
                                            100
                                        )
                                        : 0;


                                setPendingMedia(
                                    (prev) =>
                                        prev.map(
                                            (
                                                file
                                            ) =>
                                                file.id ===
                                                item.id
                                                    ? {
                                                        ...file,
                                                        progress,
                                                    }
                                                    : file
                                        )
                                );

                            },

                    }
                );


            const result =
                response.data;


            if (
                !result.success
            ) {

                throw new Error(
                    result.message ||
                    "Upload failed."
                );

            }


            setPendingMedia(
                (prev) =>
                    prev.map(
                        (file) =>
                            file.id ===
                            item.id
                                ? {
                                    ...file,
                                    status:
                                        "success",
                                    progress:
                                        100,
                                    uploaded:
                                        result.data,
                                }
                                : file
                    )
            );


            setMedia(
                (prev) => [

                    ...prev,

                    result.data,

                ]
            );


            /*
            |--------------------------------------------------------------------------
            | Remove successful pending item after a short state period.
            |--------------------------------------------------------------------------
            */

            setTimeout(
                () => {

                    setPendingMedia(
                        (prev) =>
                            prev.filter(
                                (file) =>
                                    file.id !==
                                    item.id
                            )
                    );

                },
                1200
            );


        } catch (error) {

            setPendingMedia(
                (prev) =>
                    prev.map(
                        (file) =>
                            file.id ===
                            item.id
                                ? {
                                    ...file,
                                    status:
                                        "failed",
                                    error:
                                        error?.response?.data?.message ||
                                        error.message ||
                                        "Upload failed.",
                                }
                                : file
                    )
            );

        } finally {

            setMediaUploading(false);

        }
    };


    /*
    |--------------------------------------------------------------------------
    | RETRY MEDIA
    |--------------------------------------------------------------------------
    */

    const retryMedia = (
        item
    ) => {

        if (
            !editingId
        ) {
            return;
        }

        uploadMediaFile(
            editingId,
            item
        );
    };


    /*
    |--------------------------------------------------------------------------
    | UPLOAD ALL PENDING MEDIA
    |--------------------------------------------------------------------------
    */

    const uploadPendingMedia = async (
        projectId
    ) => {

        const files =
            pendingMedia.filter(
                (item) =>
                    item.status ===
                    "pending" ||
                    item.status ===
                    "failed"
            );


        if (
            !files.length
        ) {
            return;
        }


        let completed = 0;

        setMediaUploading(true);

        setMediaOverallProgress(0);


        for (
            const item of files
        ) {

            await uploadMediaFile(
                projectId,
                item
            );

            completed++;

            setMediaOverallProgress(
                Math.round(
                    (
                        completed /
                        files.length
                    ) *
                    100
                )
            );

        }


        setMediaUploading(false);
    };


    /*
    |--------------------------------------------------------------------------
    | LOAD MEDIA
    |--------------------------------------------------------------------------
    */

    const loadMedia = async (
        projectId
    ) => {

        if (
            !projectId
        ) {
            setMedia([]);
            return;
        }


        try {

            setMediaLoading(true);


            const response =
                await api.get(
                    `/admin/projects/${projectId}/media`
                );


            const result =
                response.data;


            if (
                result.success
            ) {

                setMedia(
                    result.data ||
                    []
                );

            }

        } catch (error) {

            setMessage(
                error?.response?.data?.message ||
                error.message ||
                "Unable to load project media."
            );

        } finally {

            setMediaLoading(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | UPDATE MEDIA NAME
    |--------------------------------------------------------------------------
    */

    const updateMediaName = async (
        item
    ) => {

        const currentName =
            item.display_name ||
            item.original_name ||
            "";

        const name =
            window.prompt(
                "Enter media display name:",
                currentName
            );


        if (
            name === null ||
            name.trim() === ""
        ) {
            return;
        }


        try {

            const response =
                await api.patch(
                    `/admin/projects/${editingId}/media/${item.id}`,
                    {
                        display_name:
                            name.trim(),
                    }
                );


            const result =
                response.data;


            if (
                !result.success
            ) {
                throw new Error(
                    result.message ||
                    "Unable to update media."
                );
            }


            setMedia(
                (prev) =>
                    prev.map(
                        (mediaItem) =>
                            mediaItem.id ===
                            item.id
                                ? result.data
                                : mediaItem
                    )
            );


            setMessage(
                "Media name updated successfully."
            );

        } catch (error) {

            setMessage(
                error?.response?.data?.message ||
                error.message ||
                "Unable to update media."
            );
        }
    };


    /*
    |--------------------------------------------------------------------------
    | DELETE MEDIA
    |--------------------------------------------------------------------------
    */

    const deleteMedia = async (
        item
    ) => {

        const confirmed =
            window.confirm(
                `Delete "${item.display_name || item.original_name}"?`
            );


        if (!confirmed) {
            return;
        }


        try {

            const response =
                await api.delete(
                    `/admin/projects/${editingId}/media/${item.id}`
                );


            const result =
                response.data;


            if (
                !result.success
            ) {
                throw new Error(
                    result.message ||
                    "Unable to delete media."
                );
            }


            setMedia(
                (prev) =>
                    prev.filter(
                        (mediaItem) =>
                            mediaItem.id !==
                            item.id
                    )
            );


            showAlert(
                "success",
                "success!",
                "Media deleted successfully."
            );


        } catch (error) {

            setMessage(
                error?.response?.data?.message ||
                error.message ||
                "Unable to delete media."
            );
        }
    };


    /*
    |--------------------------------------------------------------------------
    | MEDIA DRAG/DROP
    |--------------------------------------------------------------------------
    */

    const handleMediaDragStart = (
        e,
        id
    ) => {

        setMediaDragId(id);

        e.dataTransfer.effectAllowed =
            "move";

        e.dataTransfer.setData(
            "text/plain",
            String(id)
        );
    };


    const handleMediaDragOver = (
        e,
        id
    ) => {

        e.preventDefault();

        if (
            mediaDragId &&
            mediaDragId !== id
        ) {
            setMediaDragOverId(id);
        }
    };


    const handleMediaDrop = async (
        e,
        targetId
    ) => {

        e.preventDefault();

        const sourceId =
            mediaDragId ||
            Number(
                e.dataTransfer.getData(
                    "text/plain"
                )
            );


        setMediaDragOverId(null);

        setMediaDragId(null);


        if (
            !sourceId ||
            sourceId === targetId
        ) {
            return;
        }


        const sourceIndex =
            media.findIndex(
                (item) =>
                    Number(item.id) ===
                    Number(sourceId)
            );

        const targetIndex =
            media.findIndex(
                (item) =>
                    Number(item.id) ===
                    Number(targetId)
            );


        if (
            sourceIndex === -1 ||
            targetIndex === -1
        ) {
            return;
        }


        const reordered = [
            ...media,
        ];


        const [
            moved
        ] =
            reordered.splice(
                sourceIndex,
                1
            );


        reordered.splice(
            targetIndex,
            0,
            moved
        );


        setMedia(
            reordered
        );


        try {

            const items =
                reordered.map(
                    (
                        item,
                        index
                    ) => ({

                        id:
                            item.id,

                        sort_order:
                            index,

                    })
                );


            const response =
                await api.post(
                    `/admin/projects/${editingId}/media/reorder`,
                    {
                        items,
                    }
                );


            const result =
                response.data;


            if (
                !result.success
            ) {
                throw new Error(
                    result.message ||
                    "Unable to reorder media."
                );
            }


        } catch (error) {

            setMessage(
                error?.response?.data?.message ||
                error.message ||
                "Unable to reorder media."
            );


            await loadMedia(
                editingId
            );
        }
    };


    /*
    |--------------------------------------------------------------------------
    | SAVE PROJECT
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (
        e
    ) => {

        e.preventDefault();

        setSaving(true);

        setMessage("");


        const formData =
            new FormData();


        Object.entries(
            form
        ).forEach(
            (
                [key, value]
            ) => {

                if (
                    value !== null &&
                    value !== ""
                ) {

                    formData.append(
                        key,
                        value
                    );

                }

            }
        );


        formData.set(
            "featured",
            form.featured
                ? "1"
                : "0"
        );


        formData.set(
            "active",
            form.active
                ? "1"
                : "0"
        );


        try {

            const wasEditing =
                Boolean(
                    editingId
                );


            const response =
                await api.post(

                    wasEditing
                        ? `/admin/projects/${editingId}`
                        : "/admin/projects",

                    formData,

                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }

                );


            const result =
                response.data;


            if (
                result.success === false
            ) {

                throw new Error(
                    result.message ||
                    "Unable to save project."
                );

            }


            const savedProject =
                result.data;


            /*
            |--------------------------------------------------------------------------
            | New project:
            | the project now exists, so upload pending media.
            |--------------------------------------------------------------------------
            */

            if (
                !wasEditing &&
                pendingMedia.length
            ) {

                setEditingId(
                    savedProject.id
                );


                await uploadPendingMedia(
                    savedProject.id
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Existing project:
            | media selected from the UI were uploaded immediately.
            |--------------------------------------------------------------------------
            */

            if (
                wasEditing
            ) {

                await loadMedia(
                    editingId
                );

            }


            showAlert(
                "success",
                "success!",
                wasEditing
                    ? "Project updated successfully."
                    : "Project created successfully."
            );


            setMessage(
                wasEditing
                    ? "Project updated successfully."
                    : "Project created successfully."
            );


            resetForm();


            await fetchData();


        } catch (error) {

            setMessage(
                error?.response?.data?.message ||
                error.message ||
                "Unable to save project."
            );

        } finally {

            setSaving(false);

        }
    };


    /*
    |--------------------------------------------------------------------------
    | EDIT PROJECT
    |--------------------------------------------------------------------------
    */

    const editProject = async (
        project
    ) => {

        setEditingId(
            project.id
        );


        setForm({

            impact_id:
                project.impact_id ||
                "",

            title:
                project.title ||
                "",

            tag:
                project.tag ||
                "",

            description:
                project.description ||
                "",

            content:
                project.content ||
                "",

            location:
                project.location ||
                "",

            project_year:
                project.project_year ||
                "",

            status:
                project.status ||
                "ongoing",

            featured:
                Boolean(
                    project.featured
                ),

            active:
                Boolean(
                    project.active
                ),

            image:
                null,

        });


        if (
            project.image
        ) {

            setPreview(
                `${BACKEND_URL}/storage/${project.image}`
            );

        } else {

            setPreview(null);

        }


        setPendingMedia([]);

        await loadMedia(
            project.id
        );


        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };


    /*
    |--------------------------------------------------------------------------
    | DELETE PROJECT
    |--------------------------------------------------------------------------
    */

    const deleteProject = async (
        project
    ) => {

        const confirmed =
            window.confirm(
                `Delete "${project.title}"?`
            );


        if (!confirmed) {
            return;
        }


        try {

            const response =
                await api.delete(
                    `/admin/projects/${project.id}`
                );


            const result =
                response.data;


            if (
                result.success === false
            ) {

                throw new Error(
                    result.message ||
                    "Unable to delete project."
                );

            }


            showAlert(
                "success",
                "success!",
                "Project deleted successfully."
            );


            setMessage(
                "Project deleted successfully."
            );


            await fetchData();


        } catch (error) {

            setMessage(
                error?.response?.data?.message ||
                error.message
            );
        }
    };


    /*
    |--------------------------------------------------------------------------
    | FEATURED
    |--------------------------------------------------------------------------
    */

    const toggleFeatured = async (
        project
    ) => {

        try {

            await api.patch(
                `/admin/projects/${project.id}/featured`
            );

            await fetchData();

        } catch (error) {

            setMessage(
                error?.response?.data?.message ||
                error.message
            );
        }
    };


    /*
    |--------------------------------------------------------------------------
    | MEDIA CARD
    |--------------------------------------------------------------------------
    */

    const renderMediaPreview = (
        item
    ) => {

        const url =
            getMediaUrl(item);


        if (
            item.type ===
            "image"
        ) {

            return (
                <img
                    src={url}
                    alt={
                        item.display_name ||
                        item.original_name ||
                        "Project media"
                    }
                    className="admin-project-media-preview-image"
                />
            );
        }


        if (
            item.type ===
            "video"
        ) {

            return (
                <video
                    src={url}
                    controls
                    className="admin-project-media-preview-video"
                />
            );
        }


        return (
            <div className="admin-project-media-document">
                <div className="admin-project-media-document-icon">
                    📄
                </div>

                <span>
                    {(
                        item.original_name ||
                        item.display_name ||
                        "Document"
                    ).split(".").pop().toUpperCase()}
                </span>
            </div>
        );
    };


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (
            <div className="admin-page-loading">
                Loading projects...
            </div>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <div className="admin-projects-page">

            <div className="admin-page-header">

                <div>

                    <span className="admin-page-eyebrow">
                        WEBSITE CONTENT
                    </span>

                    <h1>
                        Projects
                    </h1>

                    <p>
                        Manage constituency projects,
                        initiatives and development
                        programmes.
                    </p>

                </div>

            </div>


            {message && (
                <div className="admin-form-message">
                    {message}
                </div>
            )}


            {/* ==========================================================
                PROJECT FORM
            =========================================================== */}

            <div className="admin-panel">

                <div className="admin-panel-header">

                    <div>

                        <span>
                            {editingId
                                ? "EDIT PROJECT"
                                : "NEW PROJECT"}
                        </span>

                        <h2>
                            Project Information
                        </h2>

                    </div>

                </div>


                <form
                    className="admin-form"
                    onSubmit={
                        handleSubmit
                    }
                >

                    <div className="admin-form-row">

                        <div className="admin-form-group">

                            <label>
                                Project Title
                            </label>

                            <input
                                type="text"
                                name="title"
                                value={
                                    form.title
                                }
                                onChange={
                                    handleChange
                                }
                                required
                            />

                        </div>


                        <div className="admin-form-group">

                            <label>
                                Impact Area
                            </label>

                            <select
                                name="impact_id"
                                value={
                                    form.impact_id
                                }
                                onChange={
                                    handleChange
                                }
                            >

                                <option value="">
                                    Select Impact Area
                                </option>

                                {impacts.map(
                                    (
                                        impact
                                    ) => (

                                        <option
                                            key={
                                                impact.id
                                            }
                                            value={
                                                impact.id
                                            }
                                        >
                                            {
                                                impact.title
                                            }
                                        </option>

                                    )
                                )}

                            </select>

                        </div>

                    </div>


                    <div className="admin-form-row">

                        <div className="admin-form-group">

                            <label>
                                Category Tag
                            </label>

                            <input
                                type="text"
                                name="tag"
                                value={
                                    form.tag
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="EDUCATION"
                            />

                        </div>


                        <div className="admin-form-group">

                            <label>
                                Project Year
                            </label>

                            <input
                                type="number"
                                name="project_year"
                                value={
                                    form.project_year
                                }
                                onChange={
                                    handleChange
                                }
                                min="1900"
                                max="2200"
                            />

                        </div>

                    </div>


                    <div className="admin-form-row">

                        <div className="admin-form-group">

                            <label>
                                Location
                            </label>

                            <input
                                type="text"
                                name="location"
                                value={
                                    form.location
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="Igede-Ekiti"
                            />

                        </div>


                        <div className="admin-form-group">

                            <label>
                                Status
                            </label>

                            <select
                                name="status"
                                value={
                                    form.status
                                }
                                onChange={
                                    handleChange
                                }
                            >

                                <option value="planned">
                                    Planned
                                </option>

                                <option value="ongoing">
                                    Ongoing
                                </option>

                                <option value="completed">
                                    Completed
                                </option>

                            </select>

                        </div>

                    </div>


                    <div className="admin-form-group">

                        <label>
                            Short Description
                        </label>

                        <textarea
                            name="description"
                            value={
                                form.description
                            }
                            onChange={
                                handleChange
                            }
                            rows="4"
                        />

                    </div>


                    <div className="admin-form-group">

                        <label>
                            Full Project Content
                        </label>

                        <textarea
                            name="content"
                            value={
                                form.content
                            }
                            onChange={
                                handleChange
                            }
                            rows="8"
                        />

                    </div>


                    {/* ==================================================
                        MAIN IMAGE
                    =================================================== */}

                    <div className="admin-form-group">

                        <label>
                            Main Project Image
                        </label>

                        <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={
                                handleChange
                            }
                        />

                    </div>


                    {preview && (

                        <div className="admin-project-image-preview">

                            <img
                                src={preview}
                                alt="Project preview"
                            />

                        </div>

                    )}


                    {/* ==================================================
                        ADDITIONAL MEDIA
                    =================================================== */}

                    <div className="admin-project-media-section">

                        <div className="admin-project-media-header">

                            <div>

                                <span className="admin-page-eyebrow">
                                    PROJECT MEDIA
                                </span>

                                <h3>
                                    Images, Videos & Documents
                                </h3>

                                <p>
                                    Add multiple files to
                                    this project. Each file
                                    uploads separately.
                                </p>

                            </div>

                            {mediaUploading && (
                                <strong>
                                    Uploading...
                                </strong>
                            )}

                        </div>


                        <div className="admin-project-media-upload-box">

                            <input
                                type="file"
                                id="project-media-input"
                                multiple
                                accept="
                                    image/*,
                                    video/*,
                                    .pdf,
                                    .doc,
                                    .docx,
                                    .xls,
                                    .xlsx,
                                    .ppt,
                                    .pptx,
                                    .txt,
                                    .csv,
                                    .rtf
                                "
                                onChange={
                                    handleMediaSelection
                                }
                            />

                            <label
                                htmlFor="project-media-input"
                            >
                                <strong>
                                    + Add Project Media
                                </strong>

                                <span>
                                    Select multiple
                                    images, videos or
                                    documents
                                </span>
                            </label>

                        </div>


                        {mediaOverallProgress > 0 &&
                            mediaOverallProgress < 100 && (

                                <div className="admin-project-media-overall">

                                    <div>
                                        Overall upload:
                                        {" "}
                                        {
                                            mediaOverallProgress
                                        }%
                                    </div>

                                    <div className="admin-project-media-progress">
                                        <span
                                            style={{
                                                width:
                                                    `${mediaOverallProgress}%`,
                                            }}
                                        />
                                    </div>

                                </div>

                            )}


                        {/* ==================================================
                            NEW / UPLOADING MEDIA
                        =================================================== */}

                        {pendingMedia.length > 0 && (

                            <div className="admin-project-media-pending">

                                {pendingMedia.map(
                                    (
                                        item
                                    ) => (

                                        <div
                                            key={
                                                item.id
                                            }
                                            className={`admin-project-media-upload-item ${item.status}`}
                                        >

                                            <div className="admin-project-media-upload-thumb">

                                                {item.preview ? (

                                                    <img
                                                        src={
                                                            item.preview
                                                        }
                                                        alt=""
                                                    />

                                                ) : item.type ===
                                                    "video" ? (

                                                    <span>
                                                        🎬
                                                    </span>

                                                ) : (

                                                    <span>
                                                        📄
                                                    </span>

                                                )}

                                            </div>


                                            <div className="admin-project-media-upload-info">

                                                <strong>
                                                    {
                                                        item.display_name
                                                    }
                                                </strong>

                                                <small>
                                                    {
                                                        formatBytes(
                                                            item.file.size
                                                        )
                                                    }
                                                </small>


                                                {item.status ===
                                                    "uploading" && (

                                                    <>

                                                        <div className="admin-project-media-progress">

                                                            <span
                                                                style={{
                                                                    width:
                                                                        `${item.progress}%`,
                                                                }}
                                                            />

                                                        </div>

                                                        <small>
                                                            {
                                                                item.progress
                                                            }%
                                                        </small>

                                                    </>

                                                )}


                                                {item.status ===
                                                    "success" && (

                                                    <small>
                                                        ✓ Uploaded
                                                    </small>

                                                )}


                                                {item.status ===
                                                    "failed" && (

                                                    <small>
                                                        ✕{" "}
                                                        {
                                                            item.error
                                                        }
                                                    </small>

                                                )}

                                            </div>


                                            <div className="admin-project-media-upload-actions">

                                                {item.status ===
                                                    "failed" && (

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            retryMedia(
                                                                item
                                                            )
                                                        }
                                                    >
                                                        Retry
                                                    </button>

                                                )}


                                                {item.status !==
                                                    "uploading" && (

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            removePendingMedia(
                                                                item.id
                                                            )
                                                        }
                                                    >
                                                        Remove
                                                    </button>

                                                )}

                                            </div>

                                        </div>

                                    )
                                )}

                            </div>

                        )}


                        {/* ==================================================
                            EXISTING MEDIA
                        =================================================== */}

                        {editingId && (

                            <div className="admin-project-media-list">

                                {mediaLoading ? (

                                    <div>
                                        Loading project media...
                                    </div>

                                ) : media.length === 0 ? (

                                    <div className="admin-empty-state">
                                        No additional media added yet.
                                    </div>

                                ) : (

                                    <>

                                        <div className="admin-project-media-list-title">
                                            Existing Media
                                        </div>


                                        <div className="admin-project-media-grid">

                                            {media.map(
                                                (
                                                    item
                                                ) => (

                                                    <div
                                                        key={
                                                            item.id
                                                        }
                                                        className={`admin-project-media-card ${mediaDragId === item.id
                                                            ? "is-dragging"
                                                            : ""
                                                            } ${mediaDragOverId === item.id
                                                                ? "is-drag-over"
                                                                : ""
                                                            }`}
                                                        draggable
                                                        onDragStart={(e) =>
                                                            handleMediaDragStart(
                                                                e,
                                                                item.id
                                                            )
                                                        }
                                                        onDragOver={(e) =>
                                                            handleMediaDragOver(
                                                                e,
                                                                item.id
                                                            )
                                                        }
                                                        onDrop={(e) =>
                                                            handleMediaDrop(
                                                                e,
                                                                item.id
                                                            )
                                                        }
                                                        onDragEnd={() => {
                                                            setMediaDragId(null);
                                                            setMediaDragOverId(null);
                                                        }}
                                                    >

                                                        <div className="admin-project-media-card-preview">

                                                            {renderMediaPreview(
                                                                item
                                                            )}

                                                            <span className="admin-project-media-type">
                                                                {
                                                                    item.type
                                                                }
                                                            </span>

                                                        </div>


                                                        <div className="admin-project-media-card-content">

                                                            <strong>
                                                                {
                                                                    item.display_name ||
                                                                    item.original_name
                                                                }
                                                            </strong>

                                                            <small>
                                                                {
                                                                    formatBytes(
                                                                        item.file_size
                                                                    )
                                                                }
                                                            </small>


                                                            <div className="admin-project-media-card-actions">

                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        updateMediaName(
                                                                            item
                                                                        )
                                                                    }
                                                                >
                                                                    Rename
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="delete"
                                                                    onClick={() =>
                                                                        deleteMedia(
                                                                            item
                                                                        )
                                                                    }
                                                                >
                                                                    Delete
                                                                </button>

                                                            </div>

                                                        </div>

                                                    </div>

                                                )
                                            )}

                                        </div>

                                    </>

                                )}

                            </div>

                        )}

                    </div>


                    {/* ==================================================
                        TOGGLES
                    =================================================== */}

                    <div className="admin-project-toggles">

                        <label className="admin-toggle">

                            <input
                                type="checkbox"
                                name="featured"
                                checked={
                                    form.featured
                                }
                                onChange={
                                    handleChange
                                }
                            />

                            <span className="admin-toggle-slider" />

                            <span>

                                <strong>
                                    Featured Project
                                </strong>

                                <small>
                                    Display this project
                                    prominently.
                                </small>

                            </span>

                        </label>


                        <label className="admin-toggle">

                            <input
                                type="checkbox"
                                name="active"
                                checked={
                                    form.active
                                }
                                onChange={
                                    handleChange
                                }
                            />

                            <span className="admin-toggle-slider" />

                            <span>

                                <strong>
                                    Active
                                </strong>

                                <small>
                                    Display on the public
                                    website.
                                </small>

                            </span>

                        </label>

                    </div>


                    {/* ==================================================
                        ACTIONS
                    =================================================== */}

                    <div className="admin-career-form-actions">

                        <button
                            type="submit"
                            className="admin-primary-button"
                            disabled={
                                saving ||
                                mediaUploading
                            }
                        >

                            {saving
                                ? "Saving..."
                                : mediaUploading
                                    ? "Uploading Media..."
                                    : editingId
                                        ? "Update Project"
                                        : "Create Project"}

                        </button>


                        {editingId && (

                            <button
                                type="button"
                                className="admin-secondary-button"
                                onClick={
                                    resetForm
                                }
                            >
                                Cancel
                            </button>

                        )}

                    </div>

                </form>

            </div>


            {/* ==========================================================
                PROJECT LIST
            =========================================================== */}

            <div className="admin-panel admin-project-list">

                <div className="admin-panel-header">

                    <div>

                        <span>
                            PROJECTS
                        </span>

                        <h2>
                            All Projects
                        </h2>

                    </div>


                    <div className="admin-project-list-header-actions">

                        <span className="admin-record-count">
                            {
                                pagination.total
                            } Projects
                        </span>

                        {reordering && (
                            <span className="admin-reorder-status">
                                Saving order...
                            </span>
                        )}

                    </div>

                </div>


                {pagination.total > 0 && (

                    <div className="admin-project-order-hint">

                        <strong>
                            Drag to reorder:
                        </strong>{" "}

                        drag any project card to
                        change its position.
                        Ordering is saved automatically.

                    </div>

                )}


                <div className="admin-project-grid">

                    {projects.length === 0 ? (

                        <div className="admin-empty-state">
                            No projects have been
                            added yet.
                        </div>

                    ) : (

                        projects.map(
                            (
                                project
                            ) => (

                                <div
                                    className={`admin-project-card ${draggedId === project.id
                                        ? "is-dragging"
                                        : ""
                                        } ${dragOverId === project.id
                                            ? "is-drag-over"
                                            : ""
                                        }`}
                                    key={
                                        project.id
                                    }
                                    draggable
                                    onDragStart={(e) =>
                                        handleDragStart(
                                            e,
                                            project.id
                                        )
                                    }
                                    onDragOver={(e) =>
                                        handleDragOver(
                                            e,
                                            project.id
                                        )
                                    }
                                    onDragLeave={
                                        handleDragLeave
                                    }
                                    onDrop={(e) =>
                                        handleDrop(
                                            e,
                                            project.id
                                        )
                                    }
                                    onDragEnd={
                                        handleDragEnd
                                    }
                                >

                                    <div
                                        className="admin-project-drag-handle"
                                        title="Drag to reorder project"
                                        aria-label="Drag to reorder project"
                                    >
                                        ⋮⋮
                                    </div>


                                    <div className="admin-project-card-image">

                                        {project.image ? (

                                            <img
                                                src={`${BACKEND_URL}/storage/${project.image}`}
                                                alt={
                                                    project.title
                                                }
                                            />

                                        ) : (

                                            <div className="admin-project-placeholder">
                                                PROJECT
                                            </div>

                                        )}


                                        {project.featured && (

                                            <span className="admin-featured-badge">
                                                FEATURED
                                            </span>

                                        )}

                                    </div>


                                    <div className="admin-project-card-content">

                                        <div className="admin-project-meta">

                                            <span>
                                                {
                                                    project.tag ||
                                                    project.impact?.title
                                                }
                                            </span>

                                            <span>
                                                {
                                                    project.status
                                                }
                                            </span>

                                        </div>


                                        <h3>
                                            {
                                                project.title
                                            }
                                        </h3>


                                        <p>
                                            {
                                                project.description
                                            }
                                        </p>


                                        {project.media_count >
                                            0 && (

                                            <div className="admin-project-media-count">
                                                📎{" "}
                                                {
                                                    project.media_count
                                                } media file
                                                {project.media_count !==
                                                    1
                                                    ? "s"
                                                    : ""}
                                            </div>

                                        )}


                                        <div className="admin-project-footer">

                                            <span>
                                                {
                                                    project.project_year
                                                }
                                            </span>


                                            <div>

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        toggleFeatured(
                                                            project
                                                        )
                                                    }
                                                >
                                                    {project.featured
                                                        ? "Unfeature"
                                                        : "Feature"}
                                                </button>


                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        editProject(
                                                            project
                                                        )
                                                    }
                                                >
                                                    Edit
                                                </button>


                                                <button
                                                    type="button"
                                                    className="delete"
                                                    onClick={() =>
                                                        deleteProject(
                                                            project
                                                        )
                                                    }
                                                >
                                                    Delete
                                                </button>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            )
                        )

                    )}

                </div>


                {/* ======================================================
                    PAGINATION
                ======================================================= */}

                {pagination.total > 0 && (

                    <div className="admin-project-pagination">

                        <div className="admin-pagination-info">

                            Showing{" "}

                            <strong>
                                {
                                    pagination.from
                                }
                            </strong>

                            {" "}to{" "}

                            <strong>
                                {
                                    pagination.to
                                }
                            </strong>

                            {" "}of{" "}

                            <strong>
                                {
                                    pagination.total
                                }
                            </strong>

                            {" "}projects

                        </div>


                        <div className="admin-pagination-controls">

                            <button
                                type="button"
                                className="admin-pagination-button"
                                onClick={() =>
                                    handlePageChange(
                                        currentPage - 1
                                    )
                                }
                                disabled={
                                    currentPage === 1 ||
                                    loading
                                }
                            >
                                Previous
                            </button>


                            {getPageNumbers().map(
                                (
                                    page,
                                    index,
                                    pages
                                ) => (

                                    <React.Fragment
                                        key={page}
                                    >

                                        {index > 0 &&
                                            pages[index] -
                                            pages[index - 1] >
                                            1 && (

                                                <span className="admin-pagination-ellipsis">
                                                    …
                                                </span>

                                            )}


                                        <button
                                            type="button"
                                            className={`admin-pagination-button ${currentPage === page
                                                ? "active"
                                                : ""
                                                }`}
                                            onClick={() =>
                                                handlePageChange(
                                                    page
                                                )
                                            }
                                            disabled={
                                                loading
                                            }
                                        >
                                            {
                                                page
                                            }
                                        </button>

                                    </React.Fragment>

                                )
                            )}


                            <button
                                type="button"
                                className="admin-pagination-button"
                                onClick={() =>
                                    handlePageChange(
                                        currentPage + 1
                                    )
                                }
                                disabled={
                                    currentPage ===
                                    pagination.last_page ||
                                    loading
                                }
                            >
                                Next
                            </button>


                            <select
                                className="admin-pagination-per-page"
                                value={
                                    perPage
                                }
                                onChange={
                                    handlePerPageChange
                                }
                                aria-label="Projects per page"
                            >

                                <option value="10">
                                    10 / page
                                </option>

                                <option value="15">
                                    15 / page
                                </option>

                                <option value="25">
                                    25 / page
                                </option>

                                <option value="50">
                                    50 / page
                                </option>

                                <option value="100">
                                    100 / page
                                </option>

                            </select>

                        </div>

                    </div>

                )}

            </div>

        </div>
    );
};


export default AdminProjects;